import { useMemo, useState } from 'react';
import type { Layer } from './Controls';
import { Controls, isoDaysAgo, type ControlsState } from './Controls';
import {
  useAdminStoreNames,
  useBaselineSeries,
  useEventDays,
  useHaloEffectDaily,
  useHaloSummary,
  useHaloV2Stores,
} from './useHaloV2';
import { BaselineChart } from './BaselineChart';
import { DriversComparisonChart } from './DriversComparisonChart';
import { HaloSplitChart } from './HaloSplitChart';
import { DriversCard } from './DriversCard';
import { MixCard } from './MixCard';
import { RunsCard } from './RunsCard';
import { CoverageSweepCard } from './CoverageSweepCard';
import { DayDetailCard } from './DayDetailCard';
import { RangeSummary } from './RangeSummary';
import { CoverageBanner } from './CoverageBanner';

const DEFAULT_LAYERS: ReadonlySet<Layer> = new Set(['raw', 'confidence', 'events']);

export function HaloV2Page() {
  const [state, setState] = useState<ControlsState>({
    storeId: 0,
    platform: 'shopify',
    start: isoDaysAgo(90),
    end: isoDaysAgo(1),
    layers: DEFAULT_LAYERS,
  });
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const storesQuery = useHaloV2Stores();
  const namesQuery = useAdminStoreNames();
  const seriesQuery = useBaselineSeries(state);
  const eventDaysQuery = useEventDays(state);
  const summaryQuery = useHaloSummary(state);
  const haloEffectQuery = useHaloEffectDaily(state);
  const platformSummary = summaryQuery.data?.perPlatform[state.platform];

  const coverage = storesQuery.data?.find((s) => s.storeId === state.storeId)?.coverage[
    state.platform
  ];

  const selectedPoint = useMemo(
    () => seriesQuery.data?.find((p) => p.date === selectedDate) ?? null,
    [seriesQuery.data, selectedDate],
  );

  const hasSeries = (seriesQuery.data?.length ?? 0) > 0;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
          HaloV2 Verification
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Run baseline computations and verify HaloV2 output per store before rollout.
        </p>
      </div>

      <Controls
        stores={storesQuery.data ?? []}
        storeNames={namesQuery.data}
        state={state}
        onChange={(next) => {
          setState(next);
          setSelectedDate(null);
        }}
        coverage={coverage}
      />

      {state.storeId !== 0 && coverage !== undefined ? (
        <CoverageBanner
          storeId={state.storeId}
          start={state.start}
          end={state.end}
          coverage={coverage}
        />
      ) : null}

      {state.storeId === 0 ? (
        <EmptyState text="Select a store to begin." />
      ) : seriesQuery.isLoading ? (
        <EmptyState text="Loading baseline series…" />
      ) : (
        <div className="flex gap-4 items-start">
          <div className="flex-[3] min-w-0 space-y-4">
            {hasSeries ? (
              <>
                <RangeSummary points={seriesQuery.data ?? []} summary={platformSummary} />
                <BaselineChart
                  points={seriesQuery.data ?? []}
                  eventDays={eventDaysQuery.data ?? []}
                  layers={state.layers}
                  onSelectDay={setSelectedDate}
                />
                <HaloSplitChart points={seriesQuery.data ?? []} />
                <DriversComparisonChart daily={haloEffectQuery.data?.daily ?? []} />
              </>
            ) : (
              <EmptyState
                text={
                  coverage !== undefined
                    ? `No baseline rows for this range. Computed coverage: ${coverage.firstDate} → ${coverage.lastDate}. Trigger a backfill from the Runs panel.`
                    : 'No baseline rows for this store/platform/range — trigger a backfill from the Runs panel.'
                }
              />
            )}
          </div>
          <div className="flex-1 min-w-[260px] space-y-4">
            <MixCard summary={summaryQuery.data} isLoading={summaryQuery.isLoading} />
            <DriversCard state={state} />
            <RunsCard state={state} />
            <CoverageSweepCard />
            <DayDetailCard
              state={state}
              point={selectedPoint}
              eventDays={eventDaysQuery.data ?? []}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-10 text-center text-sm text-gray-500 dark:text-gray-400">
      {text}
    </div>
  );
}
